import React, { useCallback, useEffect, useState } from 'react';
import { isEmpty, map, size, values } from 'lodash-es';
import { Pagination } from 'antd';
import { CatalogCompany, SupplierSortKey } from '@/shared/types/catalog';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { scrollTo } from '@/shared/utils/utils';
import { SUPPLIERS_PAGE_SIZE, SUPPLIER_SORT_OPTIONS } from '@/shared/constants/sourcing-marketplace';
import NoResult from '../NoResult';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import MarketplaceSortTabs from '../MarketplaceSortTabs';
import SupplierCard from '../SupplierCard';
import Spinner from '@/components/Common/Spinner';
import { getCatalogCompanies } from '@/lib/service/catalog';
import ExplorePietraServicesBanner from '../../Common/ExplorePietraServicesBanner';
import { getCreatorHubSourcingHomePath } from '@/shared/utils/sourcing-marketplace-util';
import './index.less';

const SUPPLIER_LIST_CONTAINER_ID = 'SUPPLIER_LIST_CONTAINER_ID';

interface IProps {
  isMobile: boolean;
  vertical: VerticalName;
  suppliers: CatalogCompany[];
  category: CategoryConfig;
  page: number;
  total: number;
  useRecommendationBackend: boolean;
  recommendedSupplierIds: number[];
  categoryIds?: number[];
}

const BrowseSupplierList = ({
  isMobile,
  vertical,
  suppliers,
  category,
  page,
  total,
  useRecommendationBackend,
  recommendedSupplierIds,
  categoryIds,
}: IProps) => {
  const [displayedSuppliers, setDisplayedSuppliers] = useState<CatalogCompany[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(page);
  const [loading, setLoading] = useState<boolean>(false);
  const [noMoreToLoad, setNoMoreToLoad] = useState<boolean>(false);

  const { queryParams, setQueryParams } = useQueryParams<{}>();

  useEffect(() => {
    setDisplayedSuppliers(suppliers);
    setCurrentPage(page);
    setNoMoreToLoad(size(suppliers) < SUPPLIERS_PAGE_SIZE);
  }, [suppliers, page]);

  const onUpdatePage = (newPage: number) => {
    scrollTo(SUPPLIER_LIST_CONTAINER_ID);
    setQueryParams({ [ParamKey.PAGE]: newPage });
  };

  const onUpdateSort = (newSort: SupplierSortKey) => {
    scrollTo(SUPPLIER_LIST_CONTAINER_ID);
    setQueryParams({ [ParamKey.SORT]: newSort });
  };

  const renderNoResultContainer = () => {
    return (
      <div className="no-results-container">
        <NoResult vertical={vertical} isMobile={isMobile} />
      </div>
    );
  };

  const fetchMoreSuppliers = useCallback(async () => {
    setLoading(true);
    const nextPage = currentPage + 1;

    const params: any = {
      page: nextPage,
      size: SUPPLIERS_PAGE_SIZE,
    };

    if (categoryIds?.length > 0) {
      params.categoryIds = categoryIds;
    }

    const { list } = await getCatalogCompanies(params);
    setDisplayedSuppliers((v) => v.concat(list));
    setCurrentPage(nextPage);
    setLoading(false);
    setNoMoreToLoad(size(list) < SUPPLIERS_PAGE_SIZE);
  }, [currentPage, categoryIds]);

  const renderShowMoreButton = noMoreToLoad ? null : loading ? (
    <Spinner />
  ) : (
    <span className="show-more-button" onClick={fetchMoreSuppliers}>
      Show more suppliers
    </span>
  );

  return (
    <div id={SUPPLIER_LIST_CONTAINER_ID} className="browse-supplier-list">
      {isEmpty(suppliers) ? (
        renderNoResultContainer()
      ) : (
        <>
          <div className="sort-tabs-container">
            <MarketplaceSortTabs
              sorts={queryParams.get(ParamKey.SORT) ?? SupplierSortKey.FEATURED}
              sortOptions={values(SUPPLIER_SORT_OPTIONS)}
              applySort={(value: SupplierSortKey) => onUpdateSort(value)}
            />
            {total > 0 && (
              <span className="num-results">
                {total} result{total === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {map(displayedSuppliers, (supplier: CatalogCompany, i: number) => (
            <React.Fragment key={supplier.id}>
              <SupplierCard
                supplier={supplier}
                category={category}
                isMobile={isMobile}
                isRecommendedSupplier={recommendedSupplierIds.includes(supplier.id)}
              />
              {i === 2 && (
                <ExplorePietraServicesBanner
                  signupRedirect={getCreatorHubSourcingHomePath()}
                  utmMedium="browse_suppliers"
                />
              )}
            </React.Fragment>
          ))}
          {useRecommendationBackend ? (
            renderShowMoreButton
          ) : (
            <Pagination
              showSizeChanger={false}
              current={page + 1}
              pageSize={SUPPLIERS_PAGE_SIZE}
              className="pagination"
              total={total}
              showLessItems={true}
              onChange={(nextPage) => {
                onUpdatePage(nextPage - 1);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BrowseSupplierList;
